import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, Share2, ScrollText, QrCode } from "lucide-react";
import { toast } from "sonner";
import { api, money, regCode, EDITION_STYLE, API } from "../lib/api";
import { CelestialMap } from "../components/CelestialMap";
import { ShareCard } from "../components/ShareCard";
import { logError } from "../lib/log";

export default function RegistryDetail() {
  const { registryId } = useParams();
  const [record, setRecord] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/registries/${registryId}`)
      .then(({ data }) => setRecord(data))
      .catch((err) => { logError("Registry lookup failed", err); setError(true); });
  }, [registryId]);

  const share = async () => {
    const url = `${API}/share/${registryId}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "HYLUNER Registry", url });
        return;
      } catch (err) {
        logError("Native share cancelled or failed", err);
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Preview link copied — it unfurls with the star card");
    } catch (err) {
      logError("Clipboard write blocked", err);
      toast.success(`Preview link: ${url}`, { duration: 12000 });
    }
  };

  if (error)
    return (
      <div className="max-w-xl mx-auto px-5 py-32 text-center" data-testid="registry-not-found">
        <div className="hy-label">Not found</div>
        <h1 className="font-display text-3xl sm:text-4xl text-amber-200 mt-5">
          No registry record here
        </h1>
        <p className="text-sm text-slate-400 mt-6 leading-relaxed">
          This identifier does not match a confirmed HYLUNER registry record. It may be mistyped, or the
          record may still be awaiting payment — unpaid records stay private until they are confirmed.
        </p>
        <div className="flex flex-wrap gap-4 justify-center mt-10">
          <Link to="/explore" className="hy-btn px-7 py-3.5 text-xs uppercase" data-testid="notfound-explore-btn">
            Browse the public registry
          </Link>
          <Link to="/verify" className="hy-btn-ghost px-7 py-3.5 text-xs uppercase" data-testid="notfound-verify-btn">
            Verify an ID
          </Link>
        </div>
      </div>
    );
  if (!record) return <div className="py-40 text-center"><Loader2 className="w-6 h-6 animate-spin text-amber-300 mx-auto" /></div>;

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-10 py-24" data-testid="registry-detail-page">
      <div className="flex flex-wrap items-center gap-4">
        <span className={`text-[0.6rem] uppercase tracking-[0.2em] px-3 py-1 border rounded-full ${EDITION_STYLE[record.edition]}`}>{record.edition} Edition</span>
        <span className="hy-label">{record.registry_type} · {record.status}</span>
      </div>
      <h1 className="font-display text-4xl sm:text-5xl text-white mt-6" data-testid="registry-code">{regCode(record.registry_number)}</h1>
      <p className="font-serif-accent italic text-xl text-amber-200 mt-4" data-testid="registry-dedication">{record.dedication_name}</p>

      <div className="flex flex-wrap gap-4 mt-10">
        <Link to={`/certificate/${record.registry_id}`} className="hy-btn px-7 py-3.5 text-xs uppercase flex items-center gap-2" data-testid="detail-cert-btn">
          <ScrollText className="w-4 h-4" /> Certificate
        </Link>
        <Link to={`/verify/${record.registry_id}`} className="hy-btn-ghost px-7 py-3.5 text-xs uppercase flex items-center gap-2" data-testid="detail-verify-btn">
          <QrCode className="w-4 h-4" /> Verify
        </Link>
        <button onClick={share} className="hy-btn-ghost px-7 py-3.5 text-xs uppercase flex items-center gap-2" data-testid="detail-share-btn">
          <Share2 className="w-4 h-4" /> Share link
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-10 mt-16">
        <div className="lg:col-span-2 space-y-8">
          <CelestialMap record={record} height={420} />
          {record.payment_status === "PAID" && <ShareCard record={record} />}
          {record.dedication_message && (
            <div className="hy-card p-8">
              <div className="hy-label">Dedication Message</div>
              <p className="font-serif-accent italic text-lg text-slate-300 mt-4">“{record.dedication_message}”</p>
            </div>
          )}
          <div className="hy-card p-8">
            <div className="hy-label">Coordinate Data</div>
            <div className="hy-rule my-6" />
            <div className="grid sm:grid-cols-2 gap-5 font-mono-hy text-xs text-amber-100/90">
              {Object.entries(record.coordinates).map(([k, v]) => (
                <div key={k}>
                  <div className="text-slate-500 uppercase text-[0.6rem] tracking-widest">{k.replace(/_/g, " ")}</div>
                  <div className="mt-1">{String(v)}</div>
                </div>
              ))}
              {Object.entries(record.location_metadata).map(([k, v]) => (
                <div key={k}>
                  <div className="text-slate-500 uppercase text-[0.6rem] tracking-widest">{k.replace(/_/g, " ")}</div>
                  <div className="mt-1">{String(v)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="hy-glass p-7 space-y-4 text-sm">
            <Row k="Registry ID" v={record.registry_id} />
            <Row k="Certificate ID" v={record.certificate_id || "—"} />
            <Row k="Celestial Object" v={record.celestial_object_id} />
            <Row k="Registered" v={(record.registration_date || "").slice(0, 10) || "—"} />
            <Row k="Status" v={record.status} />
            {record.amount != null && <Row k="Amount" v={money(record.amount)} />}
            {record.recipient_name && <Row k="Recipient" v={record.recipient_name} />}
          </div>
          <div className="hy-card p-7">
            <div className="hy-label mb-4">QR Verification</div>
            <img src={`${API}/certificate/${record.registry_id}/qr.png`} alt="QR" className="w-full bg-white p-3" data-testid="detail-qr" />
          </div>
          <p className="text-[0.7rem] text-slate-500 leading-relaxed">
            This record is a private commemorative dedication. It does not represent legal ownership,
            government-recognised title or property rights over any celestial body.
          </p>
        </aside>
      </div>

      {record.audit_history?.length > 0 && (
        <div className="hy-card p-8 mt-10" data-testid="audit-history">
          <div className="hy-label">Audit History</div>
          <div className="hy-rule my-6" />
          <div className="space-y-3 font-mono-hy text-[0.7rem] text-slate-400">
            {record.audit_history.map((h) => (
              <div key={`${h.at}-${h.event}`} className="flex flex-wrap gap-4">
                <span className="text-amber-300/80">{h.at?.slice(0, 19).replace("T", " ")}</span>
                <span>{h.event}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4">
    <span className="text-slate-500 text-xs uppercase tracking-widest">{k}</span>
    <span className="text-slate-200 text-right break-all">{v}</span>
  </div>
);
